import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, Copy, Download, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/** Admin: group-trip roster for a race — every joiner with contact + travel plan. */
export default function RaceRosterDialog({ race }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = (o) => {
    setOpen(o);
    if (o) api.get(`/admin/races/${race.race_id}/roster`).then((r) => setData(r.data)).catch(() => toast.error("Could not load roster"));
  };

  const rows = data?.roster || [];
  const withPlan = rows.filter((r) => r.plan).length;

  const asText = () =>
    [`${race.title} — OSCR roster (${rows.length})`, ...rows.map((r, i) => {
      const p = r.plan || {};
      return `${i + 1}. ${r.name}${r.city ? ` (${r.city})` : ""} · ${r.email}` +
        (r.plan ? ` · ${[p.distance, p.travel_date && `arrive ${p.travel_date}`, p.return_date && `leave ${p.return_date}`, p.stay, p.notes].filter(Boolean).join(" · ")}` : " · no travel plan yet");
    })].join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText());
      setCopied(true); toast.success("Roster copied — paste into WhatsApp");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  const csv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Name", "City", "Email", "Distance", "Arriving", "Leaving", "Stay", "Notes"].join(",")];
    rows.forEach((r) => {
      const p = r.plan || {};
      lines.push([r.name, r.city, r.email, p.distance, p.travel_date, p.return_date, p.stay, p.notes].map(esc).join(","));
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    a.download = `oscr-roster-${race.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={open} onOpenChange={load}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`admin-race-roster-btn-${race.race_id}`}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5 hairline border-zinc-700 text-zinc-300 hover:border-orange-600 hover:text-white transition mt-2"
        >
          <Users size={12} className="text-orange-500" /> ROSTER · {race.joiners_count || 0}
          {race.plans_count ? <span className="text-zinc-500">· {race.plans_count} PLANS</span> : null}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 text-white rounded-none" data-testid={`admin-race-roster-dialog-${race.race_id}`}>
        <DialogHeader>
          <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase">GROUP TRIP ROSTER</p>
          <DialogTitle className="font-display text-2xl text-white tracking-wider">{race.title}</DialogTitle>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
            {rows.length} joining · {withPlan} with travel plans · {race.location}
          </p>
        </DialogHeader>

        <div className="flex gap-2">
          <button type="button" onClick={copy} data-testid={`roster-copy-${race.race_id}`} className="inline-flex items-center gap-1.5 font-display text-xs tracking-wider px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white">
            {copied ? <Check size={13} /> : <Copy size={13} />} COPY FOR WHATSAPP
          </button>
          <button type="button" onClick={csv} data-testid={`roster-csv-${race.race_id}`} className="inline-flex items-center gap-1.5 font-display text-xs tracking-wider px-3 py-2 border border-zinc-700 text-zinc-200 hover:border-zinc-500">
            <Download size={13} /> EXPORT CSV
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto hairline border-zinc-800 divide-y divide-zinc-800 mt-2">
          {rows.length === 0 && (
            <p className="p-6 text-center font-mono text-xs text-zinc-500">{data ? "Nobody has joined yet." : "Loading…"}</p>
          )}
          {rows.map((r, i) => (
            <div key={r.user_id} data-testid={`roster-row-${r.user_id}`} className="p-4 grid grid-cols-1 sm:grid-cols-12 gap-3">
              <div className="sm:col-span-4 min-w-0">
                <p className="font-display text-base text-white truncate"><span className="font-mono text-[10px] text-zinc-600 mr-2">{String(i + 1).padStart(2, "0")}</span>{r.name}</p>
                <p className="font-mono text-[10px] text-zinc-500 truncate">{r.city || "—"} · {r.email}</p>
              </div>
              <div className="sm:col-span-8 text-xs">
                {r.plan ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Cell k="DISTANCE" v={r.plan.distance} />
                    <Cell k="ARRIVING" v={r.plan.travel_date} />
                    <Cell k="LEAVING" v={r.plan.return_date} />
                    <Cell k="STAY" v={r.plan.stay} />
                    {r.plan.notes && <p className="col-span-2 sm:col-span-4 text-zinc-300 italic">“{r.plan.notes}”</p>}
                  </div>
                ) : (
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600 pt-1">No travel plan yet</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Cell({ k, v }) {
  return (
    <div className="min-w-0">
      <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">{k}</p>
      <p className="text-zinc-200 truncate mt-0.5" title={v || ""}>{v || "—"}</p>
    </div>
  );
}
